import { useEffect, useRef, useState } from 'react';
import { bilderGeladen } from '@/lib/druckBereit';
import { kannPdfTeilen } from '@/lib/druckTeilen';

// Der Drucken-Knopf der iPhone-App (29.10.2026).
//
// Am Gerät gemessen (Drucktest 29.10.2026, nach dem Umbau bestätigt): In der App vom Home-Bildschirm druckt
// nur der Weg über das Teilen-Menü. Deshalb wird das Blatt vorab als PDF
// aufbereitet — sobald Daten und Bilder da sind —, und der Knopf teilt
// die fertige Datei. Vorab, weil iOS das Teilen nur unmittelbar nach dem
// Tipp erlaubt; eine Aufbereitung erst danach käme zu spät.
//
// Der Weg über Safari bleibt als zweiter Knopf, falls das Teilen auf
// einem Gerät nicht geht.

type Zustand = 'wartet' | 'bereitet' | 'bereit' | 'fehler' | 'nicht-moeglich';

export function TeilenDruck({
  bereit,
  wurzelSelektor,
  erzeugen,
  onSafari
}: {
  /** Daten geladen, das Blatt steht am Bildschirm. */
  bereit: boolean;
  /** Wo die Bilder stehen, auf die vor der Aufnahme gewartet wird. */
  wurzelSelektor: string;
  erzeugen: () => Promise<File>;
  onSafari: () => void;
}) {
  const [zustand, setZustand] = useState<Zustand>('wartet');
  const [datei, setDatei] = useState<File | null>(null);
  const erzeugenRef = useRef(erzeugen);
  erzeugenRef.current = erzeugen;

  useEffect(() => {
    if (!bereit) return;
    if (!kannPdfTeilen()) {
      setZustand('nicht-moeglich');
      return;
    }
    let abgebrochen = false;
    setZustand('bereitet');
    void (async () => {
      await bilderGeladen(document.querySelector(wurzelSelektor));
      try {
        const f = await erzeugenRef.current();
        if (abgebrochen) return;
        setDatei(f);
        setZustand('bereit');
      } catch {
        if (!abgebrochen) setZustand('fehler');
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [bereit, wurzelSelektor]);

  async function teilen(): Promise<void> {
    if (!datei) return;
    try {
      await navigator.share({ files: [datei], title: datei.name });
    } catch {
      // Abgebrochen (AbortError) ist kein Fehler — die Datei bleibt bereit.
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {zustand !== 'nicht-moeglich' && zustand !== 'fehler' && (
        <button
          type="button"
          onClick={() => void teilen()}
          disabled={zustand !== 'bereit'}
          className="min-h-[44px] rounded-md bg-akzent px-4 text-sm text-cream disabled:opacity-50"
        >
          {zustand === 'bereit' ? 'Drucken' : 'Wird vorbereitet …'}
        </button>
      )}
      <button
        type="button"
        onClick={onSafari}
        className="min-h-[44px] rounded-md border border-line px-4 text-sm"
      >
        Über Safari drucken
      </button>
      <p className="w-full text-xs text-ink-faint">
        {zustand === 'fehler'
          ? 'Das Blatt ließ sich nicht aufbereiten. Bitte über Safari drucken.'
          : zustand === 'nicht-moeglich'
            ? 'Teilen wird auf diesem Gerät nicht angeboten. Bitte über Safari drucken.'
            : 'Im Teilen-Menü auf „Drucken" tippen.'}
      </p>
    </div>
  );
}
